import { useEffect, useState } from 'react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/data-display/Badge';
import { useWalletRecords } from './useWalletRecords';
import type { WalletRecordItem } from './types';
import { parseCalendarDate } from '@/lib/calendar-date';

export interface WalletLedgerTableProps {
  walletId: string | number;
}

const PAGE_SIZE = 20;

/** La date de valeur, ou rien tant que l'operation n'est pas reglee (#169). */
function formatValueDate(iso: string | null): string | null {
  const date = parseCalendarDate(iso);
  if (!date) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function formatAmount(amount: string, currencyCode: string): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return `${amount} ${currencyCode}`;
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currencyCode}`;
  }
}

function RowSkeleton() {
  return (
    <tr className="animate-pulse" role="status" aria-label="Loading row">
      <td className="border-b border-[#eee] px-4 py-3">
        <div className="h-3 w-24 rounded bg-[#f0f0f0]" />
      </td>
      <td className="border-b border-[#eee] px-4 py-3">
        <div className="h-5 w-16 rounded-full bg-[#f0f0f0]" />
      </td>
      <td className="border-b border-[#eee] px-4 py-3 text-right">
        <div className="ml-auto h-3 w-20 rounded bg-[#f0f0f0]" />
      </td>
      <td className="border-b border-[#eee] px-4 py-3">
        <div className="h-3 w-28 rounded bg-[#f0f0f0]" />
      </td>
    </tr>
  );
}

export function WalletLedgerTable({ walletId }: WalletLedgerTableProps) {
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<WalletRecordItem[]>([]);

  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
  }, [walletId]);

  const { data, isLoading, error } = useWalletRecords({
    walletId,
    limit: PAGE_SIZE,
    offset,
  });

  useEffect(() => {
    if (!data?.items) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((r) => r.wallet_record_id));
      const additions = data.items.filter((r) => !seen.has(r.wallet_record_id));
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, [data]);

  const records = accumulated;
  const total = data?.total ?? records.length;
  const limit = data?.limit ?? PAGE_SIZE;
  const hasMore = records.length < total;

  if (isLoading && records.length === 0) {
    return (
      <div data-testid="wallet-ledger-loading" className="rounded-xl border border-[#eee] bg-white">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-[#eee] bg-[#f8f9fa] text-left text-[11px] uppercase tracking-[1px] text-[#999]">
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Direction</th>
              <th className="px-4 py-3 text-right font-semibold">Amount</th>
              <th className="px-4 py-3 font-semibold">Transaction</th>
            </tr>
          </thead>
          <tbody>
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </tbody>
        </table>
      </div>
    );
  }

  if (error && records.length === 0) {
    return (
      <div
        data-testid="wallet-ledger-error"
        className="rounded-[10px] border border-[#ffebee] bg-[#fff5f5] p-4 text-sm text-[#c62828]"
        role="alert"
      >
        Failed to load wallet records: {error.message || 'Unknown error'}
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div
        data-testid="wallet-ledger-empty"
        className="rounded-[10px] border border-dashed border-[#ddd] bg-[#fafafa] p-6 text-center text-sm text-[#666]"
      >
        No wallet records
      </div>
    );
  }

  return (
    <div
      data-testid="wallet-ledger-table"
      className="overflow-hidden rounded-xl border border-[#eee] bg-white"
    >
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-[#eee] bg-[#f8f9fa] text-left text-[11px] uppercase tracking-[1px] text-[#999]">
            <th className="px-4 py-3 font-semibold">Date</th>
            <th className="px-4 py-3 font-semibold">Direction</th>
            <th className="px-4 py-3 text-right font-semibold">Amount</th>
            <th className="px-4 py-3 font-semibold">Transaction</th>
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <tr
              key={record.wallet_record_id}
              data-testid={`wallet-ledger-row-${String(record.wallet_record_id)}`}
              className="hover:bg-[#fafafa]"
            >
              <td className="whitespace-nowrap border-b border-[#eee] px-4 py-3 font-mono text-[#333]">
                {formatValueDate(record.value_date) ?? (
                  <span className="text-[#999] italic">En attente</span>
                )}
              </td>
              <td className="border-b border-[#eee] px-4 py-3">
                {record.direction === 'D' ? (
                  <Badge variant="error">Débit</Badge>
                ) : (
                  <Badge variant="success">Crédit</Badge>
                )}
              </td>
              <td className="whitespace-nowrap border-b border-[#eee] px-4 py-3 text-right font-mono font-semibold text-[#333]">
                {formatAmount(record.amount, record.currency_code)}
              </td>
              <td className="border-b border-[#eee] px-4 py-3">
                {record.transaction_id ? (
                  <a
                    href={`/transactions/${encodeURIComponent(record.transaction_id)}`}
                    className="font-mono text-[#1a3a5c] hover:underline"
                  >
                    {record.transaction_id}
                  </a>
                ) : (
                  <span className="text-[#999]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between border-t border-[#eee] bg-[#fafafa] px-4 py-3 text-[12px] text-[#666]">
        <span data-testid="wallet-ledger-meta">
          Showing {String(records.length)} of {String(total)} (limit {String(limit)})
        </span>
        {hasMore && (
          <Button
            size="sm"
            variant="outline"
            data-testid="wallet-ledger-load-more"
            disabled={isLoading}
            onClick={() => setOffset((current) => current + PAGE_SIZE)}
          >
            {isLoading ? 'Loading...' : 'Load more'}
          </Button>
        )}
      </div>
    </div>
  );
}
